import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Item } from "@/types/fse";
import React from "react";
import { useToggle } from "react-use";
import { useGetSourceLanguageQuery, useGetTranslationBudgetQuery, useGetTranslationPageCountQuery } from "@/api/files";
import { useSuperadmin } from "@/hooks/use-superadmin";
import { Check, CircleCheck, CircleX, TriangleAlert } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LANGUAGES } from "./translateDialog.consts";
import * as analytics from "@/services/analytics";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: (arg: { name: string; targetLang: string; sourceLang: string; isScan: boolean }) => Promise<void>;
  item: Item;
  id: string;
  err: unknown;
}

const getTranslationErrorMessage = (err: unknown): string => {
  if (typeof err === "string") return err;

  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (typeof data === "string") return data;

    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
  }

  return "An error occurred while translating the document.";
};

export const TranslateDialog: React.FC<Props> = ({ err, isOpen, id, setIsOpen, onConfirm, item }) => {
  const [targetLang, setTargetLang] = React.useState(LANGUAGES[0].value);
  const defaultName = `TRANSLATED - [${LANGUAGES[0].value}] - ${item.name}`;
  const [name, setName] = React.useState(defaultName);

  const [sourceLang, setSourceLang] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDone, setIsDone] = React.useState(false);
  const [isScan, setIsScan] = React.useState(false);
  const [hasConfirmedCost, toggleHasConfirmedCost] = useToggle(false);

  const isSuperadmin = useSuperadmin();
  const pageCountQuery = useGetTranslationPageCountQuery(id);
  const budgetQuery = useGetTranslationBudgetQuery();

  const pageCount = pageCountQuery.data?.page_count ?? null;
  const pagesRemaining = budgetQuery.data?.pages_remaining ?? null;
  const exceedsBudget = pageCount !== null && pagesRemaining !== null && pageCount > pagesRemaining;

  const getSourceLanguageQuery = useGetSourceLanguageQuery(id);
  React.useEffect(() => {
    if (getSourceLanguageQuery.data) {
      setSourceLang(getSourceLanguageQuery.data);
    }
  }, [getSourceLanguageQuery.data]);
  React.useEffect(() => {
    if (name === defaultName) {
      setName(`TRANSLATED - [${targetLang}] - ${item.name}`);
    }
  }, [targetLang, setName, name, defaultName, item.name]);

  if (err) {
    const errorMessage = getTranslationErrorMessage(err);

    return (
      <Dialog open={true} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="h-lg w-lg flex items-center justify-center">
            <CircleX className="min-h-[5rem] min-w-[5rem] rounded-full bg-red-100 text-lg text-red-400" />
          </div>
          <DialogHeader className="my-4 flex items-center justify-center">
            <DialogTitle>Translation error</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full items-center justify-center">{errorMessage}</div>
          <DialogFooter className="mt-4 flex items-center !justify-center">
            <Button type="button" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (getSourceLanguageQuery.isLoading || pageCountQuery.isLoading || budgetQuery.isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <Loader className="h-8 w-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const blockedMessage = (() => {
    if (!isSuperadmin && pageCountQuery.data && !pageCountQuery.data.page_count_known) {
      return "We could not determine how many pages this document has, so it cannot be translated. Please contact admin.";
    }
    if (!isSuperadmin && pageCountQuery.data?.exceeds_page_limit) {
      return `This document is greater than ${pageCountQuery.data.page_limit} pages, please contact admin to translate the document`;
    }
    if (!isSuperadmin && exceedsBudget) {
      return `This document is ${pageCount?.toLocaleString()} pages, but your organization has only ${pagesRemaining?.toLocaleString()} translation pages remaining this month. Please contact admin.`;
    }
    return null;
  })();

  if (blockedMessage) {
    return (
      <Dialog open={true} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="h-lg w-lg flex items-center justify-center">
            <TriangleAlert className="min-h-[5rem] min-w-[5rem] rounded-full bg-yellow-100 text-lg text-yellow-500" />
          </div>
          <DialogHeader className="my-4 flex items-center justify-center">
            <DialogTitle>Translation not allowed</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full items-center justify-center text-center">{blockedMessage}</div>
          <DialogFooter className="mt-4 flex items-center !justify-center">
            <Button type="button" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <Loader className="h-8 w-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isDone) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="h-lg w-lg flex items-center justify-center">
            <CircleCheck className="min-h-[5rem] min-w-[5rem] rounded-full bg-green-100 text-lg text-green-400" />
          </div>
          <DialogHeader className="my-4 flex items-center justify-center">
            <DialogTitle>Document has been translated successfully</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full items-center justify-center">
            Translated document will appear in your drive shortly.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!getSourceLanguageQuery.isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-red-500">An error occurred while loading the data.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-lg">
        <DialogTitle>Translate Document</DialogTitle>
        <DialogDescription>
          Set the translation options and click Translate to create a translated copy of this document.
        </DialogDescription>

        {pageCount !== null && (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            This document is <strong>{pageCount.toLocaleString()}</strong> pages.
            {pagesRemaining !== null && (
              <>
                {" "}
                Your organization has <strong>{pagesRemaining.toLocaleString()}</strong> translation pages remaining
                this month.
              </>
            )}
          </div>
        )}

        <div className="w-full">
          <Label htmlFor="title">Title</Label>
          <Input name="title" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div className="w-full">
          <Label htmlFor="source">Source</Label>
          <select
            onChange={e => setSourceLang(e.target.value)}
            defaultValue={getSourceLanguageQuery.data ?? undefined}
            className="w-full rounded border border-gray-300 px-3 py-2"
          >
            {LANGUAGES.map(lang => (
              <option key={lang.value} value={lang.value}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full">
          <Label htmlFor="source">Target</Label>
          <select
            onChange={e => setTargetLang(e.target.value)}
            defaultValue={LANGUAGES[0].value}
            className="w-full rounded border border-gray-300 px-3 py-2"
          >
            {LANGUAGES.map(lang => (
              <option key={lang.value} value={lang.value}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex w-full items-center gap-2">
          <Label>Document is scanned</Label>
          <Checkbox
            defaultChecked={false}
            onCheckedChange={checked => setIsScan(!!checked)}
            className="h-5 w-5 rounded border border-gray-300 bg-white"
          >
            <Check className="text-white" size={14} />
          </Checkbox>
        </div>

        {isSuperadmin && exceedsBudget && (
          <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            This document exceeds your organization&apos;s remaining translation allowance for this month. Translating
            it will put the organization over budget.
          </div>
        )}

        {isSuperadmin && (
          <div className="flex w-full items-start gap-2">
            <Checkbox
              aria-label="Confirm paid translation"
              defaultChecked={false}
              onCheckedChange={checked => toggleHasConfirmedCost(!!checked)}
              className="mt-0.5 h-5 w-5 rounded border border-gray-300 bg-white"
            >
              <Check className="text-white" size={14} />
            </Checkbox>
            <Label>
              I understand this translation is billed per page and will count towards the organization&apos;s monthly
              allowance
            </Label>
          </div>
        )}

        <div className="ju mt-3 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || (isSuperadmin && !hasConfirmedCost)}
            onClick={async () => {
              analytics.translateDocumentConfirm(item.name, sourceLang!, targetLang, isScan);
              setIsLoading(true);
              await onConfirm({ name, targetLang, sourceLang: sourceLang!, isScan });
              setIsLoading(false);
              setIsDone(true);
            }}
          >
            Translate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
